import { useLang } from 'hooks/lang';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';

import { loaders, SampleRecruitmentProject } from 'entities';

import { Column, IconButton, Row, Skeleton } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import {
  BadgeDollarIcon,
  CalendarClockIcon,
  DeviceIcon,
  DiamondPercentIcon,
  DocumentWithClockIcon,
  RefreshIcon,
} from 'components/Icons';

import { DEVICE_OPTIONS } from 'utils/constants';

import { useRecruitmentModal } from './context';
import { appStorage } from 'utils/storage';

const renderers: Array<{
  getLabel: (t: TFunction) => string;
  property: keyof SampleRecruitmentProject;
  extractor?: (issue: SampleRecruitmentProject & { t: TFunction }) => ReactNode;
  icon: typeof CalendarClockIcon;
}> = [
  {
    getLabel: (t) => t('device'),
    property: 'deviceTarget',
    icon: DeviceIcon,
    extractor: ({ deviceTarget, t }) =>
      DEVICE_OPTIONS.find(({ sampleId }) => sampleId === deviceTarget)?.getLabel(t),
  },

  {
    getLabel: () => 'CPI',
    property: 'cpi',
    icon: BadgeDollarIcon,
    extractor: ({ cpi, currency }) => `${currency} ${Number(cpi).toFixed(2)}`,
  },

  {
    getLabel: (t) => t('expected-ir'),
    property: 'ir',
    icon: DiamondPercentIcon,
    extractor: ({ ir }) => `${ir}%`,
  },

  {
    getLabel: (t) => t('actual-ir'),
    property: 'actualIR',
    icon: DiamondPercentIcon,
    extractor: ({ actualIR }) => actualIR ?? '0%',
  },

  {
    getLabel: () => 'LOI',
    property: 'loi',
    icon: DocumentWithClockIcon,
  },

  {
    getLabel: (t) => t('real-loi'),
    property: 'actualLOI',
    extractor: ({ actualLOI }) => actualLOI ?? 'N/A',
    icon: DocumentWithClockIcon,
  },
];

export function RecruitmentSampleStats() {
  const { t } = useLang();

  const { recruitment } = useRecruitmentModal();

  const key = `${recruitment.id}-sample`;

  const sample = loaders.projects.getRecruitmentSampleProject.use({
    defaultValue: appStorage.memory.get(key),

    params: {
      recruitmentId: recruitment.id,
    },

    onDataChange: (data) => appStorage.memory.set(key, data),

    refreshMilSeconds: 50_000,
  });

  if (!sample.data && sample.loading) return <Skeleton width="100%" height={75} />;

  const hasError = !sample.data && sample.error;

  return (
    <Column className="rounded-lg border border-primary px-4 py-2" gap="2">
      <Row justifyContent="space-between">
        <p className="font-medium text-primary">Sample</p>

        <IconButton
          size="small"
          tooltipContent="Refresh"
          onClick={sample.retry}
          disabled={sample.loading}
        >
          <RefreshIcon size={20} />
        </IconButton>
      </Row>

      {hasError ? (
        <p className="text-center">{t('failed-to-obtain-sample-data')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 small_tablet:grid-cols-1">
          {renderers.map(({ getLabel, icon: Icon, property, extractor }) => (
            <PropertyWithIcon
              key={property}
              gap="3"
              icon={<Icon size={20} />}
              name={getLabel(t)}
              value={extractor?.({ ...sample.data!, t }) ?? (sample.data?.[property] as string)}
            />
          ))}
        </div>
      )}
    </Column>
  );
}
